import * as React from 'react'
import { useEffect } from 'react'
import {
  useInvokeApi,
  usePageNotifications,
  useAuth,
  useAgencyApi,
} from '../../hooks'
import { Formik, FormikProps } from 'formik'
import { EditModalActionBar, FormikSelect } from '..'
import {
  DocumentTypeV1,
  Guid,
  LicensedAgencyConfigurationV1,
  PriorityV1,
  StatusV1,
} from '../../api'
import {
  makeStyles,
  Theme,
  createStyles,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { AsyncPayload } from '../base'
import {
  Grid,
  Form,
  Box,
  Loader,
  Typography,
  Chip,
  Autocomplete,
  TextField,
} from 'cs-kevlar-react'
import { FormikTextField } from '../FormikTextField'
import * as Yup from 'yup'
import { Option } from 'cs-kevlar-react/lib/es/Select'

interface EditNotificationConfigurationProps {
  onClose?: () => void
  licensedAgencyId: Guid
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
    chipLabel: {
      fontSize: 'small',
      color: 'black',
    },
    deletedItemText: {
      fontStyle: 'italic',
      fontSize: 'small',
    },
    autoCompleteTextField: {
      '& input::placeholder': {
        fontSize: 'small',
      },
    },
  }),
)

const minSignificantUnitsAssigned = 2
const maxSignificantUnitsAssigned = 100

export const modelSchema = Yup.object().shape({
  incidentNotificationSignificantUnitsAssigned: Yup.number()
    .nullable()
    .optional()
    .integer('The value must be an integer.')
    .typeError('The value must be an integer.')
    .min(
      minSignificantUnitsAssigned,
      'The value must be at least ' +
        minSignificantUnitsAssigned +
        ' and no more than ' +
        maxSignificantUnitsAssigned +
        '.',
    )
    .max(
      maxSignificantUnitsAssigned,
      'The value must be at least ' +
        minSignificantUnitsAssigned +
        ' and no more than ' +
        maxSignificantUnitsAssigned +
        '.',
    ),
})

export const EditNotificationConfiguration: React.FunctionComponent<EditNotificationConfigurationProps> = ({
  onClose,
  licensedAgencyId,
}) => {
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const { dispatch: dispatchNotification } = usePageNotifications()

  const {
    getLicensedAgencyConfiguration,
    editLicensedAgencyConfiguration,
    getPriorities,
    getStatuses,
  } = useAgencyApi()

  const [
    licensedAgencyConfiguration,
    setLicensedAgencyConfiguration,
  ] = React.useState<AsyncPayload<LicensedAgencyConfigurationV1>>({
    payload: null,
    isLoading: true,
  })

  const [allPriorities, setAllPriorities] = React.useState<PriorityV1[]>()

  const [selectedPriorityIds, setSelectedPriorityIds] = React.useState<
    string[]
  >([])

  const [deletedPriorities, setDeletedPriorities] = React.useState<
    PriorityV1[]
  >([])

  const [allStatuses, setAllStatuses] = React.useState<StatusV1[]>()

  const [selectedStatusIds, setSelectedStatusIds] = React.useState<string[]>([])

  const [deletedStatuses, setDeletedStatuses] = React.useState<StatusV1[]>([])

  const [allUnitStatuses, setAllUnitStatuses] = React.useState<StatusV1[]>()

  const [selectedUnitStatusIds, setSelectedUnitStatusIds] = React.useState<string[]>([])
  
  const [deletedUnitStatuses, setDeletedUnitStatuses] = React.useState<StatusV1[]>([])

  if (authUser === null) return null

  const onEdit = (config: LicensedAgencyConfigurationV1, actions: any) => {
    invokeApi(
      () => {
        if (deletedPriorities?.length > 0) {
          deletedPriorities.forEach((p) => {
            let index = config.incidentNotificationHighPriorities.indexOf(p.id)
            config.incidentNotificationHighPriorities.splice(index, 1)
          })
        }

        if (deletedStatuses?.length > 0) {
          deletedStatuses.forEach((p) => {
            let index = config.incidentNotificationStatuses.indexOf(p.id)
            config.incidentNotificationStatuses.splice(index, 1)
          })
        }

        if (deletedUnitStatuses?.length > 0) {
          deletedUnitStatuses.forEach((p) => {
            let index = config.emergencyResourceNotificationStatuses.indexOf(p.id)
            config.emergencyResourceNotificationStatuses.splice(index, 1)
          })
        }

        return editLicensedAgencyConfiguration(licensedAgencyId, config)
      },
      'There was an error updating the configuration.',
      undefined,
      () => {
        dispatchNotification({
          type: 'SetSuccess',
          payload: `Updated device configuration.`,
        })

        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  const getDeletedPriorities = async (priorityIds: string[]) => {
    await invokeApi(
      () => {
        return getPriorities(licensedAgencyId, false, priorityIds)
      },
      'There was an error getting the deleted priorities.',
      (result) => {
        setDeletedPriorities(result)
      },
    )
  }

  const getDeletedStatuses = (statusIds: string[]) => {
    invokeApi(
      () => {
        return getStatuses(
          licensedAgencyId,
          DocumentTypeV1.Incident,
          false,
          true,
          statusIds,
        )
      },
      'There was an error getting the deleted statuses.',
      (result) => {
        setDeletedStatuses(result)
      },
    )
  }

  const getDeletedUnitStatuses = (unitStatusIds: string[]) => {
    invokeApi(
        () => {
          return getStatuses(
              licensedAgencyId,
              DocumentTypeV1.EmergencyResource,
              false,
              true,
              unitStatusIds,
          )
        },
        'There was an error getting the deleted unit statuses.',
        (result) => {
          setDeletedUnitStatuses(result)
        },
    )
  }

  const handlePriorityListChange = (
    values: Option[],
    formikProps: FormikProps<LicensedAgencyConfigurationV1>,
  ) => {
    const priorityIds = values.map((x) => x.key)
    setSelectedPriorityIds(priorityIds)
    formikProps.setFieldValue('incidentNotificationHighPriorities', priorityIds)
  }

  React.useEffect(() => {
    if (selectedPriorityIds == null || selectedPriorityIds.length == 0) return

    let deleted = selectedPriorityIds.filter(
      (x) => !allPriorities?.map((y) => y.id).includes(x),
    )
    if (deleted.length > 0) {
      getDeletedPriorities(deleted)
    }
  }, [allPriorities, selectedPriorityIds])

  const priorityOptions: Option[] = React.useMemo(() => {
    if (allPriorities == null) return []

    return allPriorities.map((priority) => ({
      key: priority.id,
      value: priority.id,
      label: priority.description,
    }))
  }, [allPriorities])

  const selectedPriorityOptions = React.useMemo(() => {
    if (selectedPriorityIds == null || selectedPriorityIds.length == 0)
      return []

    return priorityOptions.filter(
      (x) => selectedPriorityIds.indexOf(x.key) > -1,
    )
  }, [priorityOptions, selectedPriorityIds])

  const handleStatusListChange = (
    values: Option[],
    formikProps: FormikProps<LicensedAgencyConfigurationV1>,
  ) => {
    const statusIds = values.map((x) => x.key)
    setSelectedStatusIds(statusIds)
    formikProps.setFieldValue('incidentNotificationStatuses', statusIds)
  }

  React.useEffect(() => {
    if (selectedStatusIds == null || selectedStatusIds.length == 0) return

    let deleted = selectedStatusIds.filter(
      (x) => !allStatuses?.map((y) => y.id).includes(x),
    )
    if (deleted.length > 0) {
      getDeletedStatuses(deleted)
    }
  }, [allStatuses, selectedStatusIds])

  const statusOptions: Option[] = React.useMemo(() => {
    if (allStatuses == null) return []

    return allStatuses.map((status) => ({
      key: status.id,
      value: status.id,
      label: status.description,
    }))
  }, [allStatuses])

  const selectedStatusOptions = React.useMemo(() => {
    if (selectedStatusIds == null || selectedStatusIds.length == 0) return []

    return statusOptions.filter((x) => selectedStatusIds.indexOf(x.key) > -1)
  }, [statusOptions, selectedStatusIds])

  const handleUnitStatusListChange = (
      values: Option[],
      formikProps: FormikProps<LicensedAgencyConfigurationV1>,
  ) => {
    const unitStatusIds = values.map((x) => x.key)
    setSelectedUnitStatusIds(unitStatusIds)
    formikProps.setFieldValue('emergencyResourceNotificationStatuses', unitStatusIds)
  }

  React.useEffect(() => {
    if (selectedUnitStatusIds == null || selectedUnitStatusIds.length == 0) return

    let deleted = selectedUnitStatusIds.filter(
        (x) => !allUnitStatuses?.map((y) => y.id).includes(x),
    )
    if (deleted.length > 0) {
      getDeletedUnitStatuses(deleted)
    }
  }, [allUnitStatuses, selectedUnitStatusIds])

  const unitStatusOptions: Option[] = React.useMemo(() => {
    if (allUnitStatuses == null) return []

    return allUnitStatuses.map((unitStatus) => ({
      key: unitStatus.id,
      value: unitStatus.id,
      label: unitStatus.description,
    }))
  }, [allUnitStatuses])

  const selectedUnitStatusOptions = React.useMemo(() => {
    console.log("selectedUnitStatusIds: ");
    console.log(selectedUnitStatusIds)
    if (selectedUnitStatusIds == null || selectedUnitStatusIds.length == 0) return []

    return unitStatusOptions.filter((x) => selectedUnitStatusIds.indexOf(x.key) > -1)
  }, [unitStatusOptions, selectedUnitStatusIds])
  
  useEffect(() => {
    const loadData = async () => {
      await invokeApi(
        () => {
          return getPriorities(licensedAgencyId, true, [])
        },
        'There was an error getting the priorities.',
        (result) => {
          setAllPriorities(result)
        },
      )

      await invokeApi(
        () => {
          return getStatuses(
            licensedAgencyId,
            DocumentTypeV1.Incident,
            true,
            true,
            [],
          )
        },
        'There was an error getting the incident statuses.',
        (result) => {
          setAllStatuses(result)
        },
      )

      await invokeApi(
          () => {
            return getStatuses(
                licensedAgencyId,
                DocumentTypeV1.EmergencyResource,
                true,
                true,
                [],
            )
          },
          'There was an error getting the unit statuses.',
          (result) => {
            setAllUnitStatuses(result)
          },
      )

      await invokeApi(
        async () => {
          return getLicensedAgencyConfiguration(licensedAgencyId)
        },
        'There was an error getting the configuration.',
        (result) => {
          setSelectedPriorityIds(
            result.incidentNotificationHighPriorities ?? [],
          )

          setSelectedStatusIds(result.incidentNotificationStatuses ?? [])

          setSelectedUnitStatusIds(result.emergencyResourceNotificationStatuses ?? [])

          setLicensedAgencyConfiguration({
            payload: result,
            isLoading: false,
          })
        },
      )
    }

    loadData()
  }, [])

  return (
    <React.Fragment>
      {licensedAgencyConfiguration.isLoading && (
        <Loader size="sm" text="Loading Configuration" />
      )}
      {licensedAgencyConfiguration.payload !== null && (
        <Formik
          initialValues={licensedAgencyConfiguration.payload}
          validationSchema={modelSchema}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column" spacing={8}>
                  <Grid item>
                    <Typography variant="body2" display="inline">
                      Several Units Assigned to a Call
                    </Typography>
                    <Grid container>
                      <Typography
                        variant="caption"
                        display="inline"
                        color="textSecondary"
                      >
                        Number of units assigned to a call to trigger an app
                        notification
                      </Typography>
                      <FormikTextField
                        id="incidentNotificationSignificantUnitsAssigned"
                        formikProps={formikProps}
                        fieldName="incidentNotificationSignificantUnitsAssigned"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid container item>
                    <Typography variant="body2" display="inline">
                      High Priorities
                    </Typography>
                    <Grid container item direction="column" spacing={2}>
                      <Grid item>
                        <Typography
                          variant="caption"
                          display="inline"
                          color="textSecondary"
                        >
                          Incidents given these priorities will trigger an app
                          notification
                        </Typography>
                      </Grid>
                      <Grid container>
                        <Autocomplete
                          fullWidth
                          multiple
                          limitTags={3}
                          disableClearable
                          disableCloseOnSelect
                          filterSelectedOptions
                          id="incidentNotificationHighPriorities"
                          options={priorityOptions}
                          value={selectedPriorityOptions}
                          getOptionLabel={(option) => option.label}
                          onChange={(event, value) =>
                            handlePriorityListChange(value, formikProps)
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                style={{ backgroundColor: '#F5F6F7' }}
                                size="small"
                                label={
                                  <Box className={classes.chipLabel}>
                                    {option.label}
                                  </Box>
                                }
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              className={classes.autoCompleteTextField}
                              placeholder="Search priorities"
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item>
                        {deletedPriorities?.length > 0 && (
                          <Typography
                            color="error"
                            className={classes.deletedItemText}
                          >
                            Priorities deleted from CAD:{' '}
                            {deletedPriorities
                              .map((x) => x.description)
                              .join(', ')}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item direction="column">
                    <Typography variant="body2" display="inline">
                      Incident Statuses
                    </Typography>
                    <Grid container item direction="column" spacing={2}>
                      <Grid item>
                        <Typography
                          variant="caption"
                          display="inline"
                          color="textSecondary"
                        >
                          Incidents given these statuses will trigger an app
                          notification
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Autocomplete
                          fullWidth
                          multiple
                          limitTags={3}
                          disableClearable
                          disableCloseOnSelect
                          filterSelectedOptions
                          id="incidentNotificationStatuses"
                          options={statusOptions}
                          value={selectedStatusOptions}
                          getOptionLabel={(option) => option.label}
                          onChange={(event, value) =>
                            handleStatusListChange(value, formikProps)
                          }
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                variant="outlined"
                                style={{ backgroundColor: '#F5F6F7' }}
                                size="small"
                                label={
                                  <Box className={classes.chipLabel}>
                                    {option.label}
                                  </Box>
                                }
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              className={classes.autoCompleteTextField}
                              placeholder="Search statuses"
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item>
                        {deletedStatuses?.length > 0 && (
                          <Typography
                            color="error"
                            className={classes.deletedItemText}
                          >
                            Statuses deleted from CAD:{' '}
                            {deletedStatuses
                              .map((x) => x.description)
                              .join(', ')}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item direction="column">
                    <Typography variant="body2" display="inline">
                      Unit Statuses
                    </Typography>
                    <Grid container item direction="column" spacing={2}>
                      <Grid item>
                        <Typography
                            variant="caption"
                            display="inline"
                            color="textSecondary"
                        >
                          Units given these statuses will trigger an app
                          notification
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Autocomplete
                            fullWidth
                            multiple
                            limitTags={3}
                            disableClearable
                            disableCloseOnSelect
                            filterSelectedOptions
                            id="emergencyResourceNotificationStatuses"
                            options={unitStatusOptions}
                            value={selectedUnitStatusOptions}
                            getOptionLabel={(option) => option.label}
                            onChange={(event, value) =>
                                handleUnitStatusListChange(value, formikProps)
                            }
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        variant="outlined"
                                        style={{ backgroundColor: '#F5F6F7' }}
                                        size="small"
                                        label={
                                          <Box className={classes.chipLabel}>
                                            {option.label}
                                          </Box>
                                        }
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    className={classes.autoCompleteTextField}
                                    placeholder="Search unit statuses"
                                    {...params}
                                />
                            )}
                        />
                      </Grid>
                      <Grid item>
                        {deletedUnitStatuses?.length > 0 && (
                            <Typography
                                color="error"
                                className={classes.deletedItemText}
                            >
                              Unit Statuses deleted from CAD:{' '}
                              {deletedUnitStatuses
                                  .map((x) => x.description)
                                  .join(', ')}
                            </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  formikProps={formikProps}
                  loader={
                    <Loader size="sm" text="Saving" disableVerticalGutters />
                  }
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
